/**
 * ClientForm Component
 *
 * Form for adding/editing clients.
 */
import { useState, useCallback, useEffect } from 'react';
import { Modal, FormField, ColorPicker, FileUpload } from '../ui';
import type { Client, ClientFormData } from '../../types';

interface ClientFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ClientFormData) => Promise<void>;
  client?: Client | null;
  isLoading?: boolean;
}

const initialFormData: ClientFormData = {
  name: '',
  color: '#0D6433',
  logo: '',
};

export const ClientForm = ({
  isOpen,
  onClose,
  onSubmit,
  client,
  isLoading = false,
}: ClientFormProps) => {
  const [formData, setFormData] = useState<ClientFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditMode = !!client;

  // Reset form when modal opens/closes or client changes
  useEffect(() => {
    if (isOpen) {
      if (client) {
        setFormData({
          name: client.name,
          color: client.color,
          logo: client.logo,
        });
      } else {
        setFormData(initialFormData);
      }
      setErrors({});
    }
  }, [isOpen, client]);

  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.color) {
      newErrors.color = 'Color is required';
    } else if (!/^#[0-9A-Fa-f]{6}$/.test(formData.color)) {
      newErrors.color = 'Invalid color format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    await onSubmit(formData);
  };

  const handleChange = (field: keyof ClientFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Client' : 'Add Client'}
      size="md"
    >
      <form onSubmit={handleSubmit}>
        <FormField
          label="Name"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          error={errors.name}
          required
          placeholder="Enter client name"
          disabled={isLoading}
        />

        <ColorPicker
          label="Brand Color"
          value={formData.color}
          onChange={(color) => handleChange('color', color)}
          error={errors.color}
        />

        <FileUpload
          label="Logo"
          value={formData.logo}
          onChange={(url) => handleChange('logo', url)}
          error={errors.logo}
        />

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-metrica-green rounded-lg hover:bg-metrica-green/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-metrica-green disabled:opacity-50"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Saving...
              </span>
            ) : isEditMode ? (
              'Update Client'
            ) : (
              'Create Client'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ClientForm;
